import { useMemo } from 'react';

import { useCurrentPage } from '@/components/Page';
import { useValidatedParams } from '@/hooks/params';
import { buildTypePredicate } from '@/util/type';

export const orderedNavigationResourceTypes = [
  'model',
  'source',
  // "column" goes here but is not an actual resource type
  'test',
  'exposure',
  'group',
  'metric',
  'semantic_model',
  'seed',
  'macro',
  'snapshot',
  'saved_query',
  // "analysis",
] as const;

export type NavigationResourceType = (typeof orderedNavigationResourceTypes)[number];

const isResourceNavOption = buildTypePredicate(orderedNavigationResourceTypes);

/**
 * Converts `unit_test` into `test` and validates the resource type
 * has a details page.
 */
export const getResourceNavOption = (
  input: string | undefined | null,
): NavigationResourceType | undefined => {
  if (input === 'unit_test') return 'test';
  return isResourceNavOption(input) ? input : undefined;
};

export const getName = (input: NavigationResourceType | 'package'): string => {
  return singularNameMap[input];
};

export const singularNameMap: Record<NavigationResourceType | 'package', string> = {
  exposure: 'exposure',
  group: 'group',
  macro: 'macro',
  metric: 'metric',
  model: 'model',
  saved_query: 'saved query',
  seed: 'seed',
  semantic_model: 'semantic model',
  snapshot: 'snapshot',
  source: 'source',
  test: 'test',
  package: 'package',
};

export const nameMap: Record<NavigationResourceType, string> = {
  // analysis: "Analyses",
  exposure: 'Exposures',
  group: 'Groups',
  macro: 'Macros',
  metric: 'Metrics',
  model: 'Models',
  saved_query: 'Saved Queries',
  seed: 'Seeds',
  semantic_model: 'Semantic Models',
  snapshot: 'Snapshots',
  source: 'Sources',
  test: 'Tests',
};

export const useSelectedResourceFilter = () => {
  const { resourceType } = useValidatedParams();
  const { currentPage } = useCurrentPage();

  return useMemo(() => {
    if (
      currentPage === 'searchResults' ||
      currentPage === 'selectResults' ||
      currentPage === 'multiProject'
    ) {
      return undefined;
    }
    return getResourceNavOption(resourceType);
  }, [currentPage, resourceType]);
};
